import React from 'react';

/**
 * ConnectionStatus — small pill indicating SSE (live stream) health.
 *
 * Three states mirror EventSource readyState:
 *   - 'connected'    — green, pulsing dot
 *   - 'connecting'   — amber, pulsing dot
 *   - 'disconnected' — red,   static dot
 *
 * Production note: the pulsing dot is decorative — `aria-live` is set
 * on the parent so screen readers announce state transitions without
 * visual reliance. Pass `lastEventAt` to surface "Last event 2s ago"
 * — operators panic less when they can see the stream IS alive.
 *
 * Phase A9 / G309 — used in LiveExecutionTab control bar.
 */
export function ConnectionStatus({ status = 'disconnected', lastEventAt, eventCount = 0 }) {
  const config = {
    connected:    { dot: 'bg-v2-success',  ring: 'ring-v2-success/30',  text: 'Connected',     pulse: true,  tone: 'text-v2-success-strong' },
    connecting:   { dot: 'bg-v2-warning',  ring: 'ring-v2-warning/30',  text: 'Connecting…',   pulse: true,  tone: 'text-v2-warning-strong' },
    disconnected: { dot: 'bg-v2-danger',   ring: 'ring-v2-danger/30',   text: 'Disconnected',  pulse: false, tone: 'text-v2-danger-strong' },
  }[status] || { dot: 'bg-v2-ink-muted', ring: '', text: status, pulse: false, tone: 'text-v2-ink-muted' };

  const lastEventLabel = lastEventAt ? formatRelative(lastEventAt) : null;

  return (
    <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-v2-surface border border-v2-border-subtle rounded-v2-pill"
         role="status" aria-live="polite">
      <span className="relative inline-flex items-center justify-center">
        <span className={`w-2 h-2 rounded-full ${config.dot}`} />
        {config.pulse && (
          <span className={`absolute inset-0 w-2 h-2 rounded-full ${config.dot} animate-ping opacity-60`} />
        )}
      </span>
      <span className={`text-[11.5px] font-semibold ${config.tone}`}>{config.text}</span>
      {eventCount > 0 && (
        <span className="text-[10.5px] text-v2-ink-muted font-mono-v2 border-l border-v2-border-subtle pl-2 ml-0.5">
          {eventCount.toLocaleString()} event{eventCount === 1 ? '' : 's'}
        </span>
      )}
      {lastEventLabel && (
        <span className="text-[10.5px] text-v2-ink-muted">
          · last {lastEventLabel}
        </span>
      )}
    </div>
  );
}

/** Tight relative-time formatter; falls back to ISO time on overflow. */
function formatRelative(ts) {
  const t = ts instanceof Date ? ts.getTime() : new Date(ts).getTime();
  if (!Number.isFinite(t)) return null;
  const diff = Date.now() - t;
  if (diff < 0) return 'now';
  if (diff < 1000) return 'just now';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

export default ConnectionStatus;
